import { useCallback } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useChangeUserPassword } from 'core/Api';
import {
  ButtonMantine,
  FullWidthButtonGroup,
  Group,
  Modal,
  PasswordInput,
  PasswordWithStrength,
  Stack,
  notifications,
  validatePassword,
} from 'ui';
import { matchesField, useForm } from 'ui/Form';

import { useChangePasswordModalProvider } from './ChangePasswordProvider';

const ChangePasswordContent = ({ onClose }: { onClose: () => void }) => {
  const form = useForm({
    initialValues: {
      password: '',
      password_confirmation: '',
      current_password: '',
    },
    validate: {
      password: validatePassword,
      password_confirmation: matchesField('password', 'Passwords must match'),
    },
    validateInputOnBlur: true,
  });

  const { mutate: changeUserPassword } = useChangeUserPassword();
  const { logIn } = useAuthentication();
  const { openModalById } = useChangePasswordModalProvider({});

  const handleChangePassword = useCallback(() => {
    if (!form.validate().hasErrors) {
      changeUserPassword(
        {
          user: {
            password: form.values.password,
            password_confirmation: form.values.password_confirmation,
            current_password: form.values.current_password,
          },
        },
        {
          onSuccess(data) {
            notifications.show({ message: 'Password Changed!', variant: 'success' });
            logIn(data);
            onClose();
          },
          onError() {
            notifications.show({
              message: 'Please check your password and try again',
              variant: 'error',
            });
          },
        }
      );
    }
  }, [form, changeUserPassword, logIn, onClose]);

  return (
    <Stack gap="md">
      <PasswordInput
        label="Enter Current Password"
        placeholder="Current password"
        {...form.getInputProps('current_password')}
      />

      <Group justify={'flex-end'}>
        <ButtonMantine
          variant="subtle"
          px={0}
          size="sm"
          onClick={() => openModalById('ResetPassword')}
        >
          Forgot Password?
        </ButtonMantine>
      </Group>

      <PasswordWithStrength
        label="Enter New password"
        placeholder="Enter password"
        confirmation={true}
        password_confirmation_props={form.getInputProps('password_confirmation')}
        {...form.getInputProps('password')}
        required={false}
      />
      <FullWidthButtonGroup>
        <ButtonMantine onClick={() => onClose()} variant={'subtle'}>
          Cancel
        </ButtonMantine>
        <ButtonMantine onClick={handleChangePassword}>Save Password</ButtonMantine>
      </FullWidthButtonGroup>
    </Stack>
  );
};

const ChangePasswordModal = () => {
  const { opened, close, props } = useChangePasswordModalProvider({
    id: 'ChangePassword',
  });

  return (
    <Modal opened={opened} onClose={() => close()} title="Change Password" {...props}>
      <ChangePasswordContent onClose={() => close()} />
    </Modal>
  );
};

export { ChangePasswordModal };
